// Recovery for "stale chunk after deploy" failures.
//
// Symptom: a tab open across a deploy holds an old bundle; its lazy imports
// reference content-hashed chunks the deploy replaced. The next route split
// or `await import("…")` 404s, the dynamic import rejects, ErrorBoundary
// renders its null fallback — user sees a blank page.
//
// Strategy: watch the three places these failures surface, filter by
// message so we don't blanket-reload on unrelated runtime errors, and force
// a single full reload to pull fresh index.html (which references the new
// chunk hashes). Pairs with the version-poll snackbar — the snackbar is the
// proactive prompt; this is the safety net for users who hit the chunk
// before they saw the prompt.

const RELOAD_GUARD_KEY = "ffair_stale_chunk_reloaded_at";
// If a reload doesn't fix the underlying error within this window, give up
// and let the real error reach the user instead of refresh-flickering.
const RELOAD_GUARD_WINDOW_MS = 30_000;

const CHUNK_ERROR_PATTERNS: RegExp[] = [
  // Chrome / Firefox / Safari messages for failed native dynamic imports.
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  // Vite's own preload-helper rejection text.
  /Unable to preload CSS/i,
  // Webpack-style messages — covers transitively-bundled libs that may
  // still emit them even though our app build is Vite.
  /Loading chunk [\w-]+ failed/i,
  /Loading CSS chunk [\w-]+ failed/i,
];

export function looksLikeStaleChunkError(input: unknown): boolean {
  if (!input) return false;
  const msg =
    input instanceof Error
      ? `${input.name}: ${input.message}`
      : typeof input === "string"
      ? input
      : (input as { message?: string })?.message ?? "";
  if (!msg) return false;
  return CHUNK_ERROR_PATTERNS.some((p) => p.test(msg));
}

export function reloadOnce(): void {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    if (Date.now() - last < RELOAD_GUARD_WINDOW_MS) return;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage can throw in private mode / disabled storage. Better
    // to risk a redundant reload than to never recover, so fall through.
  }
  window.location.reload();
}

let installed = false;

export function installStaleChunkRecovery(): void {
  if (installed) return;
  installed = true;

  // Vite's first-class hook — fires when a `<link rel="modulepreload">`
  // resolves to a 404 / network error. preventDefault() suppresses Vite's
  // default rethrow before we trigger the reload.
  window.addEventListener(
    "vite:preloadError" as keyof WindowEventMap,
    ((event: Event) => {
      event.preventDefault();
      reloadOnce();
    }) as EventListener
  );

  // A dynamic import that rejects without a Vite preload event (e.g.
  // `await import("exceljs")` from a click handler) bubbles up here.
  window.addEventListener("unhandledrejection", (event) => {
    if (looksLikeStaleChunkError(event.reason)) {
      reloadOnce();
    }
  });

  // Catches legacy `<script>` tag chunk failures — rare with Vite's pure
  // module output, but covers transitively-included webpack-style libs.
  window.addEventListener("error", (event) => {
    if (looksLikeStaleChunkError(event.error || event.message)) {
      reloadOnce();
    }
  });
}
